import { Socket } from 'socket.io-client';
import { isNull, isUndefined } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { UserRole } from '../../../constants/permittedRoles';
import { AlternateUserAccount, CustomFilters, DocumentCustomFilters, HomePageModule, OpinionCustomFilters, ReminderTypes, User } from './types';
import { ClientFeaturePermission, FeaturePermission } from '../../../admin/users/store/types';
import { isDocumentCustomFilter, isOpinionCustomFilter } from './typeAssertions';
import { getLoginRoot } from '../../store/selectors';
import { SystemMaintenance, systemMaintenanceOptions } from '../../../admin/system-maintenance/store';

export const getUser = (state: ApplicationState): User | null => getLoginRoot(state).user;

export const getAllCustomFilters = (state: ApplicationState): CustomFilters[] => getUser(state)!.customFilters || [];

export const getAllDocumentCustomFilters = (state: ApplicationState): DocumentCustomFilters[] => getAllCustomFilters(state).filter(customFilter => isDocumentCustomFilter(customFilter)) as DocumentCustomFilters[];

export const getAllOpinionCustomFilters = (state: ApplicationState): OpinionCustomFilters[] => getAllCustomFilters(state).filter(customFilter => isOpinionCustomFilter(customFilter)) as OpinionCustomFilters[];

export const getMyProfile = (state: ApplicationState): User | null => getLoginRoot(state).profileDetails;

export const getUsername = (state: ApplicationState): string | undefined => {
    const user = getUser(state);
    if (user) {
        return user.username;
    }
};

export const getUserRole = (state: ApplicationState): UserRole | undefined => {
    const user = getUser(state);
    if (user) {
        return user.role;
    }
};

export const getUserFeaturePermissions = (state: ApplicationState): number[] => {
    const user = getUser(state);
    if (user) {
        return user.featurePermissions || [];
    }
    return [];
};

export const getClientFeaturePermissions = (state: ApplicationState): number[] => {
    const user = getUser(state);
    if (user) {
        return user.clientFeaturePermissionIds || [];
    }
    return [];
};

export const getIsAdmin = (state: ApplicationState): boolean => {
    const userRole = getUserRole(state);
    if (userRole) {
        return UserRole.SYSTEM_ADMIN === userRole || UserRole.ADMIN === userRole;
    }
    return false;
};

export const getIsSystemAdmin = (state: ApplicationState): boolean => {
    const userRole = getUserRole(state);
    if (userRole) {
        return UserRole.SYSTEM_ADMIN === userRole;
    }
    return false;
};

export const getUserHasFeaturePermission = (featurePermissions: FeaturePermission[]) => (state: ApplicationState): boolean => {
    const userFeaturePermissions = getUserFeaturePermissions(state);
    return featurePermissions.every(featurePermission => userFeaturePermissions.includes(featurePermission)) || getIsAdmin(state);
};

export const getUserHasOneFeaturePermission = (featurePermissions: FeaturePermission[]) => (state: ApplicationState): boolean => {
    const userFeaturePermissions = getUserFeaturePermissions(state);
    return featurePermissions.some(featurePermission => userFeaturePermissions.includes(featurePermission)) || getIsAdmin(state);
};

export const getUserHasFeaturePermissionNoAdmin = (featurePermissions: FeaturePermission[]) => (state: ApplicationState): boolean => {
    const userFeaturePermissions = getUserFeaturePermissions(state);
    return featurePermissions.every(featurePermission => userFeaturePermissions.includes(featurePermission)) || getIsSystemAdmin(state);
};

export const getUserHasOneFeaturePermissionNoAdmin = (featurePermissions: FeaturePermission[]) => (state: ApplicationState): boolean => {
    const userFeaturePermissions = getUserFeaturePermissions(state);
    return featurePermissions.some(featurePermission => userFeaturePermissions.includes(featurePermission)) || getIsSystemAdmin(state);
};

export const getClientHasFeaturePermission = (featurePermissions: ClientFeaturePermission[]) => (state: ApplicationState): boolean => {
    const clientFeaturePermissions = getClientFeaturePermissions(state);
    return featurePermissions.every(featurePermission => clientFeaturePermissions.includes(featurePermission)) || getIsSystemAdmin(state);
};

export const getInternetGatewayOpen = (state: ApplicationState): boolean => {
    const user = getUser(state);
    return user && user.systemMaintenanceIds.includes(SystemMaintenance.INTERNET_GATEWAY_OPEN) || false;
};

export const getUserId = (state: ApplicationState): number | null => getUser(state)?.userId || null;

export const getIsLoggedIn = (state: ApplicationState): boolean => getLoginRoot(state).isLoggedIn;

export const getLoginError = (state: ApplicationState): string | null => getLoginRoot(state).loginError;

export const getCheckAuth = (state: ApplicationState): boolean => getLoginRoot(state).checkingAuth;

export const getPathname = (state: ApplicationState): string => state.router.location?.pathname || '';

export const getIsLoggingOut = (state: ApplicationState): boolean => getLoginRoot(state).loggingOut;

export const getReminderType = (state: ApplicationState): ReminderTypes | null => getLoginRoot(state).reminderType;

export const getReminderError = (state: ApplicationState): string | null => getLoginRoot(state).reminderError;

export const getSocket = (state: ApplicationState): Socket | null => getLoginRoot(state).socket;

export const getLogoutWarningModalOpen = (state: ApplicationState): boolean => getLoginRoot(state).logoutWarningModalOpen;

export const getProfileModalOpen = (state: ApplicationState): boolean => getLoginRoot(state).profileModalOpen;

export const getIsEditing = (state: ApplicationState): boolean => getLoginRoot(state).editingProfile;

export const getProfileHasUpdated = (state: ApplicationState): boolean => getLoginRoot(state).profileHasUpdated;

export const getUpdatingProfile = (state: ApplicationState): boolean => getLoginRoot(state).updatingProfile;

export const getIsSwitchingAccounts = (state: ApplicationState): boolean => getLoginRoot(state).switchingAccounts;

export const getSwitchAccountError = (state: ApplicationState): string | null => getLoginRoot(state).switchAccountError;

export const getSwitchUsername = (state: ApplicationState): string | null => getLoginRoot(state).switchUsername;
export const getSwitchPassword = (state: ApplicationState): string => getLoginRoot(state).switchPassword;

export const getShowAccountSwitchButton = (state: ApplicationState): boolean => {
    const user = getUser(state);
    return !isNull(user) && !isUndefined(user.alternateUserAccounts) && user.alternateUserAccounts.length > 0;
};

export const getAlternateAccounts = (state: ApplicationState): AlternateUserAccount[] => {
    const user = getUser(state);
    return !isNull(user) && !isUndefined(user.alternateUserAccounts) && user.alternateUserAccounts || [];
};

export const getIdleCheckTokenValid = (state: ApplicationState): boolean => getLoginRoot(state).idleCheckTokenValid;

export const getSendingReminder = (state: ApplicationState): boolean => getLoginRoot(state).sendingReminder;

export const getShowResetPassword = (state: ApplicationState): boolean => getLoginRoot(state).showResetPassword;

export const getShowSwitchAccount = (state: ApplicationState): boolean => getLoginRoot(state).showSwitchAccount;

export const getPathsDownForMaintenance = (state: ApplicationState): string[] => {
    const user = getUser(state);
    const systemMaintenanceIds = user && user.systemMaintenanceIds || [];
    return systemMaintenanceIds.reduce((acc: string[], cur) => {
        if (systemMaintenanceOptions[cur] && !isUndefined(systemMaintenanceOptions[cur].route)) {
            acc = [...acc, ...systemMaintenanceOptions[cur].route!];
        }
        return acc;
    }, []);
};

export const getHomeScreenModules = (state: ApplicationState): HomePageModule[] => {
    let modules: HomePageModule[] = [];
    const userHasDocumentsModule = getClientHasFeaturePermission([ClientFeaturePermission.DOCUMENT_MODULE])(state);
    const userHasOpinionsModule = getClientHasFeaturePermission([ClientFeaturePermission.OPINION_MODULE])(state);
    const isAdminUser = getIsAdmin(state);
    if (userHasDocumentsModule) {
        modules.push(HomePageModule.DOCUMENTS);
    }
    if (userHasOpinionsModule) {
        modules.push(HomePageModule.OPINIONS);
    }
    if (isAdminUser) {
        modules.push(HomePageModule.ADMIN);
    }
    return modules;
};
